import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import { baixarArquivoProtegido } from '../downloadArquivo';
import './estilodash.css';

const Suporte = () => {
  const [arquivos, setArquivos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [substituir, setSubstituir] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [removendo, setRemovendo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [configuracao, setConfiguracao] = useState({
    extensoes: [],
    tamanhoMaximoMb: 50,
  });
  const inputRef = useRef(null);

  const fetchArquivos = useCallback(async () => {
    try {
      setCarregando(true);
      const response = await api.get('/api/admin/arquivos');
      setArquivos(response.data?.arquivos || []);
      setConfiguracao({
        extensoes: response.data?.extensoes_permitidas || [],
        tamanhoMaximoMb: response.data?.tamanho_maximo_mb || 50,
      });
    } catch (error) {
      console.error('Erro ao listar arquivos para download:', error);
      setErro(true);
      setMensagem(error.response?.data?.message || 'Erro ao carregar os arquivos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    fetchArquivos();
  }, [fetchArquivos]);

  const formatarTamanho = (bytes) => {
    const value = Number(bytes || 0);
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatarData = (value) => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  const selecionarArquivo = (file) => {
    setMensagem('');
    setErro(false);

    if (!file) {
      setArquivoSelecionado(null);
      return;
    }

    const extension = `.${file.name.split('.').pop() || ''}`.toLowerCase();
    if (
      configuracao.extensoes.length > 0 &&
      !configuracao.extensoes.includes(extension)
    ) {
      setErro(true);
      setMensagem(`Extensão não permitida. Use: ${configuracao.extensoes.join(', ')}.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    if (file.size > configuracao.tamanhoMaximoMb * 1024 * 1024) {
      setErro(true);
      setMensagem(`O arquivo excede o limite de ${configuracao.tamanhoMaximoMb} MB.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setArquivoSelecionado(file);
    setSubstituir(false);
  };

  const enviarArquivo = async () => {
    if (!arquivoSelecionado || enviando) return;

    try {
      setEnviando(true);
      setProgresso(0);
      setErro(false);
      setMensagem('');

      const response = await api.post(
        `/api/admin/arquivos?substituir=${substituir ? '1' : '0'}`,
        arquivoSelecionado,
        {
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-File-Name': encodeURIComponent(arquivoSelecionado.name),
          },
          onUploadProgress: (event) => {
            if (event.total) {
              setProgresso(Math.round((event.loaded * 100) / event.total));
            }
          },
        }
      );

      setMensagem(response.data?.message || 'Arquivo enviado com sucesso.');
      setArquivoSelecionado(null);
      setSubstituir(false);
      setProgresso(100);
      if (inputRef.current) inputRef.current.value = '';
      await fetchArquivos();
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      setErro(true);
      setMensagem(error.response?.data?.message || 'Erro ao enviar arquivo.');
    } finally {
      setEnviando(false);
    }
  };

  const baixarArquivo = async (nome) => {
    try {
      setErro(false);
      setMensagem('');
      await baixarArquivoProtegido(nome);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      setErro(true);
      setMensagem(error.response?.data?.message || 'Erro ao baixar arquivo.');
    }
  };

  const removerArquivo = async (nome) => {
    const confirmou = window.confirm(
      `Remover permanentemente o arquivo "${nome}"? Ele deixará de aparecer para os clientes.`
    );
    if (!confirmou) return;

    try {
      setRemovendo(nome);
      setErro(false);
      setMensagem('');
      const response = await api.delete(`/api/admin/arquivos/${encodeURIComponent(nome)}`);
      setMensagem(response.data?.message || 'Arquivo removido com sucesso.');
      await fetchArquivos();
    } catch (error) {
      console.error('Erro ao remover arquivo:', error);
      setErro(true);
      setMensagem(error.response?.data?.message || 'Erro ao remover arquivo.');
    } finally {
      setRemovendo('');
    }
  };

  return (
    <div className="container-suporte">
      <h2>Contato e Suporte</h2>

      <div className="suporte-grid">
        {/* Coluna da esquerda */}
        <div className="suporte-col">
          <div className="suporte-secao">
            <h3>Informações de Contato</h3>
            <p>
              📱{' '}
              <a
                href="https://wa.me/5542984241307"
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp: (42) 98424-1307
              </a>
            </p>
            <p>
              📧{' '}
              <a href="mailto:contato@quantumtrades.com.br">
                contato@quantumtrades.com.br
              </a>
            </p>
            <p>
              📧{' '}
              <a href="mailto:suporte@quantumtrades.com.br">
                suporte@quantumtrades.com.br
              </a>{' '}
              (Suporte)
            </p>
            <p>
              📺{' '}
              <a
                href="https://www.youtube.com/@QuantumTradesBR"
                target="_blank"
                rel="noreferrer"
              >
                YouTube
              </a>
            </p>
            <p>
              📨{' '}
              <a
                href="https://t.me/QuantumTradesBr"
                target="_blank"
                rel="noreferrer"
              >
                Canal Telegram Aberto
              </a>
            </p>
            <p>
              👥{' '}
              <a
                href="https://t.me/+tdXvf6jsLSZmZjlh"
                target="_blank"
                rel="noreferrer"
              >
                Grupo Telegram VIP para Assinantes
              </a>
            </p>
          </div>

        </div>

        {/* Coluna da direita */}
        <div className="suporte-col">
          <div className="suporte-secao">
            <h3>Recomendação de Corretora Forex</h3>
            <p>
              💹 Recomendamos a <strong>Exness</strong> para operações em Forex:
              <br />
              <a
                href="https://one.exnesstrack.org/a/hkga5cflzh"
                target="_blank"
                rel="noreferrer"
                style={{ fontWeight: 'bold' }}
              >
                Clique aqui para abrir sua conta Exness
              </a>
            </p>
          </div>
        </div>
      </div>

      <section className="admin-file-manager" aria-labelledby="admin-file-manager-title">
        <div className="admin-file-manager-header">
          <div>
            <h3 id="admin-file-manager-title">Gerenciador de Arquivos</h3>
            <p>Arquivos liberados na área de Downloads dos clientes.</p>
          </div>
          <button
            type="button"
            className="qt-btn-secondary admin-file-refresh"
            onClick={fetchArquivos}
            disabled={carregando || enviando}
          >
            Atualizar lista
          </button>
        </div>

        <div className="admin-file-upload">
          <label
            className={`admin-file-dropzone ${arrastando ? 'is-dragging' : ''}`}
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setArrastando(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setArrastando(false)}
            onDrop={(event) => {
              event.preventDefault();
              setArrastando(false);
              selecionarArquivo(event.dataTransfer.files?.[0]);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept={configuracao.extensoes.join(',')}
              onChange={(event) => selecionarArquivo(event.target.files?.[0])}
              disabled={enviando}
            />
            <span>Selecionar arquivo</span>
            <small>
              {configuracao.extensoes.join(', ') || 'Extensões configuradas no servidor'} · até{' '}
              {configuracao.tamanhoMaximoMb} MB
            </small>
          </label>

          <div className="admin-file-upload-controls">
            <div className="admin-file-selection">
              {arquivoSelecionado ? (
                <>
                  <strong>{arquivoSelecionado.name}</strong>
                  <span>{formatarTamanho(arquivoSelecionado.size)}</span>
                </>
              ) : (
                <span>Nenhum arquivo selecionado.</span>
              )}
            </div>

            <label className="admin-file-replace">
              <input
                type="checkbox"
                checked={substituir}
                onChange={(event) => setSubstituir(event.target.checked)}
                disabled={!arquivoSelecionado || enviando}
              />
              Substituir arquivo com o mesmo nome
            </label>

            <button
              type="button"
              className="qt-btn-primary admin-file-send"
              onClick={enviarArquivo}
              disabled={!arquivoSelecionado || enviando}
            >
              {enviando ? 'Enviando...' : 'Enviar arquivo'}
            </button>
          </div>

          {enviando && (
            <div className="admin-file-progress" aria-live="polite">
              <progress max="100" value={progresso} />
              <span>{progresso}%</span>
            </div>
          )}
        </div>

        {mensagem && (
          <div className={`admin-file-message ${erro ? 'is-error' : 'is-success'}`} role="status">
            {mensagem}
          </div>
        )}

        <div className="admin-file-list-header">
          <strong>{arquivos.length} arquivo(s)</strong>
        </div>

        {carregando ? (
          <p className="admin-file-empty">Carregando arquivos...</p>
        ) : arquivos.length === 0 ? (
          <p className="admin-file-empty">Nenhum arquivo disponível.</p>
        ) : (
          <div className="admin-file-table-wrap">
            <table className="admin-file-table">
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Tipo</th>
                  <th>Tamanho</th>
                  <th>Atualizado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {arquivos.map((arquivo) => (
                  <tr key={arquivo.nome}>
                    <td className="admin-file-name">{arquivo.nome}</td>
                    <td>{arquivo.extensao || '—'}</td>
                    <td>{formatarTamanho(arquivo.tamanho)}</td>
                    <td>{formatarData(arquivo.atualizado_em)}</td>
                    <td>
                      <div className="admin-file-actions">
                        <button
                          type="button"
                          className="qt-btn-secondary"
                          onClick={() => baixarArquivo(arquivo.nome)}
                        >
                          Baixar
                        </button>
                        <button
                          type="button"
                          className="qt-btn-danger"
                          onClick={() => removerArquivo(arquivo.nome)}
                          disabled={removendo === arquivo.nome}
                        >
                          {removendo === arquivo.nome ? 'Removendo...' : 'Excluir'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Suporte;
